import { Box, Button, Group, Input } from '@mantine/core';
import { IconVariable } from '@tabler/icons-react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { type Editor, EditorContent, type JSONContent, Node, useEditor } from '@tiptap/react';
import StarterKit, { type StarterKitOptions } from '@tiptap/starter-kit';
import { themedMantineInput } from 'init/mantine/components/Input';
import { i18nKeys, useTranslation } from 'locales';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { identity, isEmpty, isNullish } from 'remeda';
import styles from './TemplateSubjectInput.module.scss';
import {
  type TemplateVariableItem,
  useGetVariableCategoryLabel,
  useGetVariableLabel,
  useTemplateVariableCategories,
} from './use-template-variables';
import {
  type SuggestionPopupAnchor,
  VARIABLE_SLASH_TRIGGER,
  VariableSuggestionPopup,
} from './VariableSuggestionList';
import { splitOnVariables, VARIABLE_PILL_CLASS } from './variable-serialization';

type TemplateSubjectInputProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  planType: string;
};

type VariablePickerRange = {
  from: number;
  to: number;
  anchor: SuggestionPopupAnchor;
};

type VariablePicker =
  | (VariablePickerRange & { mode: 'insert' | 'button' })
  | (VariablePickerRange & { mode: 'edit'; variable: string });

export const TemplateSubjectInput = ({
  label,
  value,
  onChange,
  planType,
}: TemplateSubjectInputProps) => {
  const { t } = useTranslation();
  const categories = useTemplateVariableCategories(planType);
  const getVariableLabel = useGetVariableLabel(categories);
  const getVariableCategoryLabel = useGetVariableCategoryLabel(categories);
  const [picker, setPicker] = useState<VariablePicker | null>(null);

  const getVariableLabelRef = useRef(getVariableLabel);
  getVariableLabelRef.current = getVariableLabel;

  const editor = useEditor({
    extensions: [
      SingleLineDocument,
      StarterKit.configure(PLAIN_TEXT_STARTER_KIT),
      TemplateVariable.configure({ getLabel: variable => getVariableLabelRef.current(variable) }),
    ],
    content: toDocument(value),
    editorProps: {
      attributes: { role: 'textbox', 'aria-label': label },
      handleClickOn: (view, _pos, node, nodePos) => {
        const variablePicker = toVariablePicker(view, node, nodePos);
        if (isNullish(variablePicker)) return false;

        setPicker(variablePicker);
        return true;
      },
      handleClick: () => {
        setPicker(null);
        return false;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      onChange(updatedEditor.getText());
      setPicker(findSlashTrigger(updatedEditor));
    },
  });

  useEffect(() => {
    if (isNullish(editor) || editor.getText() === value) return;

    editor.commands.setContent(toDocument(value), false);
    setPicker(null);
  }, [editor, value]);

  const applyVariable = (item: TemplateVariableItem) => {
    if (editor && picker) {
      const variableNode = { type: TemplateVariable.name, attrs: { value: item.value } };

      editor
        .chain()
        .focus()
        .insertContentAt(
          { from: picker.from, to: picker.to },
          picker.mode === 'edit' ? variableNode : [variableNode, { type: 'text', text: ' ' }],
        )
        .run();
    }

    setPicker(null);
  };

  const dismissPicker = () => {
    if (editor && picker?.mode === 'insert') {
      editor.chain().focus().deleteRange({ from: picker.from, to: picker.to }).run();
    } else {
      editor?.commands.focus();
    }

    setPicker(null);
  };

  const openPickerFromButton = (button: HTMLButtonElement) => {
    if (isNullish(editor)) return;

    const { from, to } = editor.state.selection;
    setPicker({ mode: 'button', from, to, anchor: button.getBoundingClientRect() });
  };

  const focusEditorFromPadding = (event: MouseEvent<HTMLDivElement>) => {
    if (isNullish(editor)) return;
    if (event.target instanceof Element && editor.view.dom.contains(event.target)) return;

    event.preventDefault();
    editor.commands.focus('end');
  };

  return (
    <Input.Wrapper label={label} labelProps={{ onClick: () => editor?.commands.focus('end') }}>
      <Input
        component="div"
        styles={themedMantineInput}
        classNames={{ input: styles.subjectInput }}
        onMouseDown={focusEditorFromPadding}
      >
        <Group gap="xs" wrap="nowrap">
          <Box flex={1} miw={0}>
            <EditorContent editor={editor} />
          </Box>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconVariable size={16} stroke={1.5} />}
            onMouseDown={keepEditorSelection}
            onClick={event => openPickerFromButton(event.currentTarget)}
          >
            {t(i18nKeys.ADD_VARIABLE)}
          </Button>
        </Group>
      </Input>

      {picker && (
        <VariableSuggestionPopup
          anchor={picker.anchor}
          categories={categories}
          initialCategoryLabel={
            picker.mode === 'edit' ? getVariableCategoryLabel(picker.variable) : null
          }
          command={applyVariable}
          onClose={dismissPicker}
        />
      )}
    </Input.Wrapper>
  );
};

const toVariablePicker = (
  view: EditorView,
  node: ProseMirrorNode,
  nodePos: number,
): VariablePicker | null => {
  if (node.type.name !== TemplateVariable.name) return null;

  const pill = view.nodeDOM(nodePos);
  if (!(pill instanceof HTMLElement)) return null;

  return {
    mode: 'edit',
    from: nodePos,
    to: nodePos + node.nodeSize,
    anchor: pill.getBoundingClientRect(),
    variable: String(node.attrs.value),
  };
};

const keepEditorSelection = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const LINE_BREAKS = /[\r\n]+/g;

const VARIABLE_AS_WORD_BOUNDARY = ' ';

const PLAIN_TEXT_STARTER_KIT: Partial<StarterKitOptions> = {
  document: false,
  blockquote: false,
  bold: false,
  bulletList: false,
  code: false,
  codeBlock: false,
  dropcursor: false,
  gapcursor: false,
  hardBreak: false,
  heading: false,
  horizontalRule: false,
  italic: false,
  listItem: false,
  orderedList: false,
  strike: false,
};

type TemplateVariableOptions = {
  getLabel: (value: string) => string;
};

const TemplateVariable = Node.create<TemplateVariableOptions>({
  name: 'templateVariable',
  group: 'inline',
  inline: true,
  atom: true,

  addOptions() {
    return { getLabel: identity() };
  },

  addAttributes() {
    return { value: { default: '' } };
  },

  renderHTML({ node }) {
    const variable = String(node.attrs.value);

    return [
      'span',
      { class: VARIABLE_PILL_CLASS, 'data-variable': variable },
      this.options.getLabel(variable),
    ];
  },

  renderText({ node }) {
    return `{{${String(node.attrs.value)}}}`;
  },
});

const SingleLineDocument = Node.create({
  name: 'doc',
  topNode: true,
  content: 'paragraph',

  addKeyboardShortcuts() {
    return { Enter: () => true };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste: (_view, event) => {
            const text = event.clipboardData?.getData('text/plain') ?? '';
            if (isEmpty(text)) return false;

            return this.editor.commands.insertContent(
              toInlineContent(text.replace(LINE_BREAKS, ' ')),
            );
          },
        },
      }),
    ];
  },
});

const toInlineContent = (text: string): JSONContent[] =>
  splitOnVariables(text).map(segment =>
    segment.type === 'variable'
      ? { type: TemplateVariable.name, attrs: { value: segment.value } }
      : { type: 'text', text: segment.text },
  );

const toDocument = (text: string): JSONContent => ({
  type: 'doc',
  content: [{ type: 'paragraph', content: toInlineContent(text) }],
});

const findSlashTrigger = (editor: Editor): VariablePicker | null => {
  const { selection } = editor.state;
  if (!selection.empty) return null;

  const { $from } = selection;
  const textBeforeCursor = $from.parent.textBetween(
    0,
    $from.parentOffset,
    undefined,
    VARIABLE_AS_WORD_BOUNDARY,
  );
  if (!VARIABLE_SLASH_TRIGGER.test(textBeforeCursor)) return null;

  const from = $from.pos - 1;
  return { mode: 'insert', from, to: $from.pos, anchor: editor.view.coordsAtPos(from) };
};
